import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Edit2, Trash2, ArrowLeft, FileDown } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { formatCurrency, formatDateRange } from "@/lib/formatters";
import { generateEventPDF, reconcileEvent } from "@/lib/pdfGenerator";
import * as db from "@/lib/db";

const EventDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [activityTypes, setActivityTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [downloadingPDF, setDownloadingPDF] = useState(false);

  useEffect(() => {
    fetchEvent();
  }, [id]);

  const fetchEvent = async () => {
    try {
      setLoading(true);
      const [data, ats] = await Promise.all([
        db.getEvent(id),
        db.listActivityTypes({ includeArchived: true }),
      ]);
      setEvent(data);
      setActivityTypes(ats);
    } catch (error) {
      console.error("Error fetching event:", error);
      toast.error("Failed to load event");
      navigate("/events");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await db.deleteEvent(id);
      toast.success("Event deleted successfully");
      navigate("/events");
    } catch (error) {
      console.error("Error deleting event:", error);
      toast.error("Failed to delete event");
    }
  };

  const handleDownloadPDF = async () => {
    if (!event || downloadingPDF) return;

    if (!reconcileEvent(event)) {
      toast.error("Unable to generate P&L PDF because the event totals do not reconcile. Please review the event details.");
      return;
    }

    setDownloadingPDF(true);
    const toastId = toast.loading("Generating Management P&L PDF...");

    try {
      // Small delay so the toast is visible
      await new Promise((r) => setTimeout(r, 50));
      generateEventPDF(event);
      toast.success("Management P&L PDF downloaded successfully.", { id: toastId });
    } catch (error) {
      console.error("PDF generation failed:", error);
      toast.error("Unable to generate the Management P&L PDF. Please try again.", { id: toastId });
    } finally {
      setDownloadingPDF(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-6">
        <p className="text-slate-500 text-center py-12">Loading event...</p>
      </div>
    );
  }

  if (!event) return null;

  // Group products by category
  const groupedProducts = (event.products || []).reduce((acc, p) => {
    if (!acc[p.category]) acc[p.category] = [];
    acc[p.category].push(p);
    return acc;
  }, {});

  const profit = event.netProfit || 0;
  const profitColor = profit > 0 ? "text-emerald-700" : profit < 0 ? "text-red-600" : "text-slate-600";

  return (
    <div className="min-h-screen bg-[#FAFAFA] pb-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-6 space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <Link
            to="/events"
            className="flex items-center gap-2 text-slate-600 hover:text-[#193f7d] text-sm font-medium"
            data-testid="back-to-events"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Link>
        </div>

        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-[#193f7d]" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="event-detail-name">
                {event.eventName}
              </h1>
              {event.status === "draft" && (
                <span className="text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 border border-amber-200 rounded-full px-2 py-0.5" data-testid="event-detail-status-badge">
                  Draft
                </span>
              )}
            </div>
            {event.activityTypeId && (
              <p className="text-sm font-semibold text-[#fa9d1c] mt-1" data-testid="event-detail-activity-type">
                {activityTypes.find((a) => a.id === event.activityTypeId)?.name || "—"}
              </p>
            )}
            <p className="text-slate-600 mt-1" data-testid="event-detail-dates">
              {formatDateRange(event.startDate, event.endDate)} · {event.totalEventDays} {event.totalEventDays === 1 ? "day" : "days"}
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button
              onClick={handleDownloadPDF}
              disabled={downloadingPDF}
              className="bg-[#193f7d] hover:bg-[#122f5e] text-white rounded-full font-semibold h-11 px-5 transition-transform active:scale-95"
              data-testid="download-pdf-btn"
            >
              <FileDown className="w-4 h-4 mr-2" />
              {downloadingPDF ? "Generating..." : "Download P&L PDF"}
            </Button>
            <Button
              onClick={() => navigate(`/events/${id}/edit`)}
              variant="outline"
              className="border border-slate-200 hover:border-[#193f7d] hover:text-[#193f7d] rounded-full font-medium h-11 px-5 bg-white"
              data-testid="edit-event-btn"
            >
              <Edit2 className="w-4 h-4 mr-2" />
              Edit
            </Button>
            <Button
              onClick={() => setShowDeleteDialog(true)}
              variant="outline"
              className="border border-red-200 text-red-600 hover:border-red-400 hover:text-red-700 hover:bg-red-50 rounded-full font-medium h-11 px-5 bg-white"
              data-testid="delete-event-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </Button>
          </div>
        </div>

        {/* Event Info */}
        {event.generalNotes && (
          <section className="bg-white border border-slate-200 rounded-2xl p-5 sm:p-6">
            <h2 className="text-lg font-medium text-slate-800 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>General Notes</h2>
            <p className="text-slate-600 whitespace-pre-wrap" data-testid="event-general-notes">{event.generalNotes}</p>
          </section>
        )}

        {/* Products Sold */}
        <section className="bg-white border border-slate-200 rounded-2xl p-5 sm:p-6 space-y-4" data-testid="detail-products-section">
          <h2 className="text-lg font-medium text-slate-800" style={{ fontFamily: 'Outfit, sans-serif' }}>Products Sold</h2>

          {Object.keys(groupedProducts).length === 0 ? (
            <p className="text-slate-500 text-center py-4">No products recorded</p>
          ) : (
            Object.entries(groupedProducts).map(([category, products]) => (
              <div key={category} className="space-y-3">
                <h3 className="text-xs font-bold uppercase tracking-wider text-[#193f7d]">{category}</h3>
                {products.map((p, idx) => (
                  <div key={idx} className="border border-slate-200 rounded-xl p-4 bg-slate-50 space-y-3" data-testid={`detail-product-${p.productId}`}>
                    <div className="flex items-center justify-between gap-3">
                      <p className="font-medium text-slate-900">{p.productName}</p>
                      <span className="text-xs text-slate-500">per {p.unit}</span>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      <DetailField label="Qty Sold" value={p.quantitySold} isNumber />
                      <DetailField label="Unit Cost" value={formatCurrency(p.unitCost)} />
                      <DetailField label="Total Sales" value={formatCurrency(p.totalSalesAmount)} />
                      <DetailField label="Product Cost" value={formatCurrency(p.productCost)} />
                    </div>

                    <div className="flex items-center justify-between pt-2 border-t border-slate-200">
                      <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Gross Profit</span>
                      <span className={`font-mono font-semibold ${p.productGrossProfit >= 0 ? "text-emerald-700" : "text-red-600"}`}>
                        {formatCurrency(p.productGrossProfit)}
                      </span>
                    </div>

                    {p.remark && (
                      <div className="pt-2 border-t border-slate-200">
                        <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Remark</p>
                        <p className="text-sm text-slate-600 mt-1">{p.remark}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ))
          )}

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-4 border-t border-slate-200">
            <DetailField label="Total Qty Sold" value={event.totalProductQuantitySold || 0} isNumber emphasized />
            <DetailField label="Total Recorded Product Sales" value={formatCurrency(event.totalRecordedProductSales != null ? event.totalRecordedProductSales : event.totalSales)} emphasized />
            <DetailField label="Total Product Cost" value={formatCurrency(event.totalProductCost)} emphasized />
            <DetailField label="Total Gross Profit" value={formatCurrency(event.totalProductGrossProfit)} emphasized />
          </div>
        </section>

        {/* Sales Reconciliation */}
        <section className="bg-white border border-slate-200 rounded-2xl p-5 sm:p-6 space-y-4" data-testid="detail-reconciliation-section">
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-lg font-medium text-slate-800" style={{ fontFamily: 'Outfit, sans-serif' }}>Sales Reconciliation</h2>
            <span className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider ${
              event.reconciliationStatus === "Fully Reconciled" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" :
              event.reconciliationStatus === "Unrecorded Cup Sales Estimated" ? "bg-amber-50 text-amber-700 border border-amber-200" :
              event.reconciliationStatus === "Recorded Product Sales Exceed Total Sales Collected" ? "bg-red-50 text-red-700 border border-red-200" :
              "bg-slate-100 text-slate-600 border border-slate-200"
            }`} data-testid="detail-recon-status">
              {event.reconciliationStatus || "Manual Review Required"}
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <DetailField label="Total Sales Collected" value={formatCurrency(event.totalSalesCollected)} emphasized />
            <DetailField label="Total Recorded Product Sales" value={formatCurrency(event.totalRecordedProductSales != null ? event.totalRecordedProductSales : event.totalSales)} emphasized />
            <DetailField label="Unrecorded Sales Amount" value={formatCurrency(event.unrecordedSalesAmount)} emphasized />
            <DetailField label="Standard Cup Selling Price" value={formatCurrency(event.standardCupSellingPrice)} />
            <DetailField label="Raw Unrecorded Cup Equivalent" value={`${(event.rawUnrecordedCupEquivalent || 0).toFixed(2)} cups`} isNumber />
            <DetailField label="Estimated Whole Cups (for Costing)" value={`${event.estimatedUnrecordedWholeCups || 0} ${(event.estimatedUnrecordedWholeCups || 0) === 1 ? "cup" : "cups"}`} isNumber />
            <DetailField label="Conservative Cup Unit Cost" value={formatCurrency(event.conservativeCupUnitCost)} />
            <DetailField label="Estimated Unrecorded COGS" value={formatCurrency(event.estimatedUnrecordedCogs)} emphasized />
            <DetailField label="Total Recorded Product Cost" value={formatCurrency(event.totalProductCost)} />
            <DetailField label="Total Cost of Goods Sold" value={formatCurrency(event.totalCostOfGoodsSold != null ? event.totalCostOfGoodsSold : event.totalProductCost)} emphasized />
          </div>

          <div className="pt-3 border-t border-slate-200">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Rounding Method</p>
            <p className="text-sm text-slate-700 mt-1">{event.roundingMethod || "Rounded upward to the next whole cup"}</p>
          </div>

          {(event.unrecordedSalesAmount || 0) > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-xs text-amber-800" data-testid="detail-recon-note">
              Certain standard Cup Herbal Tea sales were not recorded under Products Sold. The exact product mix was unavailable. The estimated cup equivalent was rounded upward to the next whole cup for conservative costing.
            </div>
          )}
        </section>

        {/* Staff Wages */}
        {event.staffWages && event.staffWages.length > 0 && (
          <section className="bg-white border border-slate-200 rounded-2xl p-5 sm:p-6 space-y-3" data-testid="detail-staff-section">
            <h2 className="text-lg font-medium text-slate-800" style={{ fontFamily: 'Outfit, sans-serif' }}>Staff Wages</h2>

            {event.staffWages.map((s, idx) => (
              <div key={idx} className="border border-slate-200 rounded-xl p-4 bg-slate-50" data-testid={`detail-staff-${idx}`}>
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="font-medium text-slate-900">{s.staffName}</p>
                    {s.remark && <p className="text-sm text-slate-600 mt-1">{s.remark}</p>}
                  </div>
                  <p className="font-mono font-semibold text-slate-900">{formatCurrency(s.wageAmount)}</p>
                </div>
              </div>
            ))}

            <div className="flex items-center justify-between pt-3 border-t border-slate-200">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-700">Total Staff Wages</span>
              <span className="font-mono font-bold text-slate-900" data-testid="detail-total-staff-wages">{formatCurrency(event.totalStaffWages)}</span>
            </div>
          </section>
        )}

        {/* Other Costs */}
        <section className="bg-white border border-slate-200 rounded-2xl p-5 sm:p-6 space-y-4" data-testid="detail-other-costs-section">
          <h2 className="text-lg font-medium text-slate-800" style={{ fontFamily: 'Outfit, sans-serif' }}>Other Event Costs</h2>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <DetailField label="Rental Cost" value={formatCurrency(event.rentalCost)} />
            <DetailField label="Transport Cost" value={formatCurrency(event.transportCost)} />
            <DetailField label="Other Cost" value={formatCurrency(event.otherCost)} />
          </div>

          {event.otherCostRemark && (
            <div className="pt-3 border-t border-slate-200">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Other Cost Remark</p>
              <p className="text-sm text-slate-600 mt-1 whitespace-pre-wrap">{event.otherCostRemark}</p>
            </div>
          )}
        </section>

        {/* Profit & Loss */}
        <section className="bg-gradient-to-br from-[#193f7d] to-[#122f5e] text-white rounded-2xl p-5 sm:p-6 space-y-4 shadow-lg" data-testid="detail-pl-section">
          <h2 className="text-lg font-medium text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>Profit & Loss</h2>

          <div className="flex items-center justify-between py-2 border-b border-white/20">
            <span className="text-white/80">Total Revenue (Sales Collected)</span>
            <span className="font-mono font-semibold text-white text-lg" data-testid="detail-total-revenue">
              {formatCurrency(event.totalSalesCollected != null ? event.totalSalesCollected : event.totalSales)}
            </span>
          </div>

          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-white/60 mb-2">Less: Cost of Goods Sold</p>
            <div className="space-y-2 pl-2">
              <PLRow label="Recorded Product Cost" value={formatCurrency(event.totalProductCost)} />
              <PLRow label="Estimated COGS — Unrecorded Cup Sales" value={formatCurrency(event.estimatedUnrecordedCogs || 0)} />
              <div className="flex items-center justify-between border-t border-white/20 pt-2">
                <span className="text-white/90 text-sm font-semibold">Total Cost of Goods Sold</span>
                <span className="font-mono font-semibold text-white text-sm" data-testid="detail-total-cogs">
                  {formatCurrency(event.totalCostOfGoodsSold != null ? event.totalCostOfGoodsSold : event.totalProductCost)}
                </span>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between py-2 border-t border-white/20">
            <span className="text-white font-medium">Gross Profit</span>
            <span className="font-mono font-bold text-white" data-testid="detail-gross-profit">
              {formatCurrency(
                (event.totalSalesCollected != null ? event.totalSalesCollected : event.totalSales) -
                  (event.totalCostOfGoodsSold != null ? event.totalCostOfGoodsSold : event.totalProductCost)
              )}
            </span>
          </div>

          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-white/60 mb-2">Less: Operating Expenses</p>
            <div className="space-y-2 pl-2">
              <PLRow label="Rental Cost" value={formatCurrency(event.rentalCost)} />
              <PLRow label="Total Staff Wages" value={formatCurrency(event.totalStaffWages)} />
              <PLRow label="Transport Cost" value={formatCurrency(event.transportCost)} />
              <PLRow label="Other Cost" value={formatCurrency(event.otherCost)} />
            </div>
          </div>

          <div className="flex items-center justify-between py-3 border-t-2 border-white/40">
            <span className="text-white font-medium">Net Profit</span>
            <span className={`text-2xl font-bold font-mono ${profit >= 0 ? "text-[#fa9d1c]" : "text-red-300"}`} data-testid="detail-net-profit">
              {formatCurrency(profit)}
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-3 border-t border-white/20">
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-white/60">Net Profit Margin</p>
              <p className="font-mono font-semibold text-white mt-1" data-testid="detail-profit-margin">{(event.profitMargin || 0).toFixed(2)}%</p>
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-white/60">Avg Sales / Day</p>
              <p className="font-mono font-semibold text-white mt-1" data-testid="detail-avg-sales">{formatCurrency(event.averageSalesPerDay)}</p>
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-white/60">Avg Net Profit / Day</p>
              <p className={`font-mono font-semibold mt-1 ${event.averageNetProfitPerDay >= 0 ? "text-[#fa9d1c]" : "text-red-300"}`} data-testid="detail-avg-net-profit">
                {formatCurrency(event.averageNetProfitPerDay)}
              </p>
            </div>
          </div>
        </section>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#193f7d]" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="delete-dialog-title">
              Are you sure you want to delete this event?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-slate-600">
              This will permanently remove "{event.eventName}" and all its recorded data. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border border-slate-200 hover:border-[#193f7d] hover:text-[#193f7d] rounded-full h-11 px-6 bg-white" data-testid="cancel-delete-btn">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700 text-white rounded-full h-11 px-6 font-semibold"
              data-testid="confirm-delete-btn"
            >
              Delete Event
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const DetailField = ({ label, value, isNumber = false, emphasized = false }) => (
  <div>
    <p className="text-xs font-bold uppercase tracking-wider text-slate-500">{label}</p>
    <p className={`${isNumber ? "" : "font-mono"} ${emphasized ? "font-bold text-slate-900" : "font-semibold text-slate-800"} mt-1`}>
      {value}
    </p>
  </div>
);

const PLRow = ({ label, value }) => (
  <div className="flex items-center justify-between">
    <span className="text-white/70 text-sm">{label}</span>
    <span className="font-mono text-white/95 text-sm">{value}</span>
  </div>
);

export default EventDetails;
